import { createAsyncThunk } from "@reduxjs/toolkit";
import { APP_MULTI_USER_API } from "../../../services/api/constants";
import {
  createStorageObject,
  getAllStorageObjects,
  updateStorageObject,
} from "../../../services/api/instances/storageApi/methods";
import type { UserSettingsValue } from "../../../services/api/instances/useSettingsApi/types";
import { getUserInfo, login } from "../../../services/api/methods";
import type { AuthUserType } from "../../../services/api/methodsTypes";
import type { RootState } from "../../../services/redux/types";
import { multiUserApi } from "../../../shared/utils/multiUserFunctions";
import type { AuthType, CredentialsType } from "./types";
import { StorageObject } from "../../../services/api/instances/storageApi/types/object_types";
import { getError } from "../../../services/lib/getError";

export const getAllSettingsObjectsAction = createAsyncThunk(
  "auth/getAllSettingsObjects",
  async (username: string, { rejectWithValue }) => {
    try {
      const settingsObjects =
        await getAllStorageObjects<UserSettingsValue>("usersettings");
      const settingsObject =
        settingsObjects.find((object) => object.friendly_name === username) ??
        null;
      return { settingsObjects, settingsObject };
    } catch (e: unknown) {
      const message = getError(e);
      return rejectWithValue(message ?? "An unknown error occurred");
    }
  },
);

export const authAction = createAsyncThunk(
  "auth/authAction",
  async (
    {
      credentials,
      authType,
    }: { credentials: CredentialsType; authType: AuthType },
    { rejectWithValue },
  ) => {
    const { username, password } = credentials;
    try {
      const loginData = await login(username, password);
      const multiUser: AuthUserType = {
        user_name: username,
        access_token: loginData.access_token,
        refresh_token: loginData.refresh_token,
        isActive: true,
        replacement_token: "",
      };

      if (authType === "app") {
        multiUserApi.addUser(multiUser);
      } else if (authType === "login") {
        localStorage.setItem(APP_MULTI_USER_API, JSON.stringify([multiUser]));
      }

      const userInfo = await getUserInfo();
      const groups = userInfo["user-groups"].filter(
        (group) => group !== "/artifacts",
      );
      const settingsObjects =
        await getAllStorageObjects<UserSettingsValue>("usersettings");
      const isUserSettingsExist = settingsObjects.find(
        (user) => user.friendly_name === userInfo.preferred_username,
      );
      const userSettingsObject = isUserSettingsExist
        ? isUserSettingsExist
        : await createStorageObject<UserSettingsValue>(
            {
              friendly_name: userInfo.preferred_username,
              value: JSON.stringify({
                user_settings: {
                  app_links: groups.slice(0, 6),
                },
              }),
            },
            "usersettings",
          );
      if (authType === "login" && userInfo.replacement_token) {
        localStorage.setItem(
          APP_MULTI_USER_API,
          multiUserApi.setUserKey(
            multiUserApi.parseToArray(),
            "replacement_token",
            userInfo.replacement_token,
          ),
        );
      }
      return { userInfo, userSettingsObject };
    } catch (e: unknown) {
      throw new Error(e as string);
    }
  },
);

export const updateUserSettingsAction = createAsyncThunk(
  "auth/updateUserSettings",
  async (settings: UserSettingsValue, ThunkAPI) => {
    try {
      const { settingsObject } = (ThunkAPI.getState() as RootState).authState;
      const updatedSettings = {
        ...settingsObject,
        value: settings,
      } as StorageObject<UserSettingsValue>;

      return await updateStorageObject<UserSettingsValue>(
        updatedSettings,
        "usersettings",
      );
    } catch (e: unknown) {
      throw new Error(e as string);
    }
  },
);
